import * as v from "valibot";
import { cn } from "~/lib/cn";
import type { UserSignupForm } from "~/lib/schemas/user-auth";
import { codeSchema, UserSignupSchema } from "~/lib/schemas/user-auth";
import { updateUser } from "~/lib/user.db";
import { routeLoader$, useNavigate } from "@builder.io/qwik-city";
import { $, useStore, useSignal, component$ } from "@builder.io/qwik";
import { isClerkAPIResponseError } from "~/lib/clerk";
import ImageLogoHorizontal from "~/img/logo/dcm-horizontal-1.png?w=203;406&jsx";

import {
  submit,
  useForm,
  getValue,
  setValue,
  validate,
  valiForm$,
  formAction$,
} from "@modular-forms/qwik";

import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { FormStep } from "~/components/ui/form-step";
import { OtpInput } from "~/components/ui/otp-input";
import { FormField } from "~/components/ui/form-field";

import SocialMedia from "~/img/social-media.svg?jsx";
import InterfaceSuccess from "~/img/interface-success.svg?jsx";

import type { QRL } from "@builder.io/qwik";
import type { InitialValues } from "@modular-forms/qwik";
import { useClerk } from "~/hooks/use-clerk";

export const useFormAction = formAction$<UserSignupForm>(
  async (values, context) => {
    try {
      const userId = context.cookie.get("userId")?.value as string;
      if (!userId) {
        context.redirect(302, "/discovery");
      }

      await updateUser(userId, values);
      return {
        status: "success",
        message: "Preferences updated",
      };
    } catch (error: unknown) {
      return {
        status: "error",
        message: `Failed to save update user: ${error}`,
      };
    }
  },
  valiForm$(UserSignupSchema),
);

export const useFormLoader = routeLoader$<InitialValues<UserSignupForm>>(
  () => ({
    email: "",
    phoneNumber: undefined,
    password: "",
    password2: "",
    clerkUserId: "",
  }),
);

type SignUpProps = {
  isModalView?: boolean;
  onSuccess?: QRL<() => void>;
};
export const SignUp = component$<SignUpProps>(
  ({ onSuccess, isModalView = false }) => {
    const nav = useNavigate();
    const [form, { Field, Form }] = useForm<UserSignupForm>({
      loader: useFormLoader(),
      action: useFormAction(),
      validate: valiForm$(UserSignupSchema),
    });
    const clerk = useClerk();

    const isProcessing = useSignal(false);
    const state = useStore({
      code: ["", "", "", "", "", ""],
      error: "",
      resendTimer: 60,
      canResend: false,
    });

    // OTP handlers
    const startResendTimer = $(() => {
      state.resendTimer = 60;
      state.canResend = false;
      const timer = setInterval(() => {
        state.resendTimer--;
        if (state.resendTimer <= 0) {
          state.canResend = true;
          clearInterval(timer);
        }
      }, 1000);
    });

    const currentStepIndex = useSignal(0);

    const handleSendOtp = $(async (formState: typeof form) => {
      if (!(await validate(formState))) return;
      startResendTimer();
      state.error = "";
      isProcessing.value = true;
      const emailAddress = getValue(formState, "email");
      const password = getValue(formState, "password");

      try {
        if (!clerk.value?.client) return;

        await clerk.value.client.signUp.create({ emailAddress, password });
        await clerk.value.client.signUp.prepareEmailAddressVerification();

        isProcessing.value = false;
        currentStepIndex.value = 1;
      } catch (err) {
        console.log(err);
        if (isClerkAPIResponseError(err)) {
          state.error =
            err.errors[0]?.longMessage || "Failed to create sign up attempt";
        } else {
          state.error = "An unexpected error occurred.";
        }
        isProcessing.value = false;
      }
    });

    const handleValidateCode = $(async () => {
      try {
        v.parse(codeSchema, { code: state.code });
        state.error = "";
        return true;
      } catch (error: unknown) {
        if (error instanceof v.ValiError) {
          state.error = error.issues?.[0]?.message;
        } else {
          state.error = "Invalid input.";
        }
        return false;
      }
    });

    const handleVerifyOtp = $(async () => {
      if (!(await handleValidateCode())) return;
      isProcessing.value = true;

      try {
        if (!clerk.value?.client) return;

        const signUpAttempt =
          await clerk.value.client.signUp.attemptEmailAddressVerification({
            code: state.code.join(""),
          });

        if (signUpAttempt.status !== "complete") {
          isProcessing.value = false;
          return;
        }
        setValue(form, "clerkUserId", signUpAttempt.createdUserId as string);
        submit(form);
        await clerk.value.setActive({
          session: signUpAttempt.createdSessionId,
        });

        isProcessing.value = false;
        currentStepIndex.value = 2;
      } catch (err) {
        isProcessing.value = false;
        if (isClerkAPIResponseError(err)) {
          state.error =
            err.errors[0]?.longMessage || "Failed to verify OTP code";
        } else {
          state.error = "An unexpected error occurred.";
        }

        state.error = "Failed to verify OTP code";
      }
    });

    const handleSuccess = $(async () => {
      if (onSuccess) {
        onSuccess();
      } else {
        nav("/discovery/steps/processing");
      }
    });

    const handleOtpChange = $((code: string[]) => {
      state.code = [...code];
    });

    const handleOtpComplete = $((code: string) => {
      state.code = code.split("");
      handleVerifyOtp();
    });

    return (
      <div class="flex flex-col items-center justify-center gap-6">
        <ImageLogoHorizontal class="w-[203px]" />
        <Form>
          <Field name="clerkUserId" type="string">
            {(field, props) => (
              <Input
                {...props}
                type="hidden"
                name={field.name}
                value={field.value}
              />
            )}
          </Field>
          <Card
            class={cn("sm:w-145 w-100 flex flex-col p-6 hover:shadow", {
              "border-none p-0 shadow-none hover:shadow-none": isModalView,
            })}
          >
            <FormStep.Content stepPosition={currentStepIndex.value}>
              <div class="w-full flex-shrink-0 space-y-2 px-1">
                <div class="text-center">
                  <SocialMedia class="mx-auto" />

                  <h1 class="font-heading text-3xl">Unlock your dream coach</h1>
                  <h3 class="text-secondary font-heading">
                    Create a free account to see all coaches we found for you
                    and connect with them
                  </h3>
                </div>
                <Field name="email">
                  {(field, props) => (
                    <div class="space-y-2">
                      <FormField
                        fullWidth
                        label="Enter email or phone number"
                        error={field.error || state.error}
                      >
                        <Input
                          {...props}
                          type="email"
                          placeholder="Enter email or phone number"
                          value={field.value}
                          error={field.error}
                        />
                      </FormField>
                    </div>
                  )}
                </Field>
                <Field name="password">
                  {(field, props) => (
                    <div class="space-y-2">
                      <FormField
                        fullWidth
                        label="Create Password"
                        error={field.error}
                      >
                        <Input
                          {...props}
                          type="password"
                          placeholder="Create Password"
                          value={field.value}
                          error={field.error}
                        />
                      </FormField>
                    </div>
                  )}
                </Field>
                <Field name="password2">
                  {(field, props) => (
                    <div class="space-y-2">
                      <FormField
                        fullWidth
                        label="Confirm Password"
                        error={field.error}
                      >
                        <Input
                          {...props}
                          type="password"
                          placeholder="Confirm Password"
                          value={field.value}
                          error={field.error}
                        />
                      </FormField>
                    </div>
                  )}
                </Field>
                <Button
                  type="button"
                  loading={isProcessing.value}
                  disabled={isProcessing.value}
                  class="mb-2 w-full"
                  onClick$={() => handleSendOtp(form)}
                >
                  Create Account & Continue
                </Button>
                <div id="clerk-captcha" class="w-full" />
              </div>
              <div class="w-full flex-shrink-0 px-1">
                <div class="space-y-2">
                  <h1 class="font-heading text-3xl">Check your email</h1>
                  <h3 class="text-secondary font-heading">
                    We sent a code to your email. Please enter it below to
                    confirm your account.
                  </h3>
                </div>
                <div class="my-8 flex items-end gap-4 pl-1 transition-all duration-300 ease-in-out">
                  <OtpInput
                    value={state.code}
                    onChange$={handleOtpChange}
                    onComplete$={handleOtpComplete}
                    error={state.error}
                  />
                </div>
                <Button
                  type="button"
                  class="mb-2 w-full"
                  onClick$={() => handleSendOtp(form)}
                  disabled={!state.canResend}
                  loading={isProcessing.value}
                >
                  {state.canResend
                    ? "Resend code"
                    : `Resend code (${state.resendTimer}s)`}
                </Button>
              </div>
              <div class="w-full flex-shrink-0 space-y-2 px-1">
                <div class="text-center">
                  <InterfaceSuccess class="mx-auto" />

                  <h1 class="font-heading text-3xl">Account created!</h1>
                  <h3 class="text-secondary font-heading">
                    You’re all set! Click below to see all your matches.
                  </h3>
                </div>

                <Button
                  type="button"
                  class="mb-2 w-full"
                  onClick$={() => handleSuccess()}
                >
                  See my coach matches
                </Button>
                <div id="clerk-captcha" class="w-full" />
              </div>
            </FormStep.Content>
            <div class="font-400 mt-4 text-center text-sm">
              Already have an account?{" "}
              <a
                href="/signin"
                class="text-primary/100 font-600 hover:underline"
              >
                Log in
              </a>
            </div>
          </Card>
        </Form>
      </div>
    );
  },
);

export default SignUp;
